import React from 'react';
import type { SurveyAnalytics } from '../types';
import '../research-tools-overlay.css';

interface Props {
  analytics: SurveyAnalytics;
  onClose: () => void;
}

function questionTypeLabel(type: string): string {
  const map: Record<string, string> = {
    text: 'Открытый текст',
    single: 'Один вариант',
    multi: 'Множественный выбор',
    scale: 'Шкала',
    matrix: 'Матрица',
    date: 'Дата',
  };
  return map[type] || type;
}

/** Survey analytics — template research-tools modal shell. */
export const AnalyticsModal: React.FC<Props> = ({ analytics, onClose }) => {
  return (
    <div
      className="research-tools-modal-wrap open"
      role="dialog"
      aria-modal="true"
      aria-label="Аналитика исследования"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="research-tools-modal" style={{ width: 'min(920px, 100%)' }}>
        <header>
          <div>
            <small>Аналитика исследования</small>
            <h2>{analytics.title}</h2>
            <p>Сводка ответов и распределение по вопросам</p>
          </div>
          <button type="button" className="research-tools-btn" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </header>

        <div className="research-tools-modal-body">
          <div className="research-tools-kpis">
            <article className="research-tools-kpi" style={{ ['--rt-accent' as string]: '#41d2ff' }}>
              <small>Получено ответов</small>
              <strong>{analytics.total_responses}</strong>
            </article>
            <article className="research-tools-kpi" style={{ ['--rt-accent' as string]: '#49ddb7' }}>
              <small>Приглашено</small>
              <strong>{analytics.invited_count}</strong>
            </article>
            <article className="research-tools-kpi" style={{ ['--rt-accent' as string]: '#e2ad3a' }}>
              <small>Доля ответов</small>
              <strong>{analytics.response_rate}%</strong>
            </article>
            <article className="research-tools-kpi" style={{ ['--rt-accent' as string]: '#9e80ff' }}>
              <small>Средний балл шкалы</small>
              <strong>{analytics.mean_scale_score}</strong>
            </article>
          </div>

          <section className="research-tools-panel" style={{ marginTop: 0 }}>
            <header>
              <div>
                <h3>Распределение ответов по вопросам</h3>
                <p>По каждому вопросу формы</p>
              </div>
            </header>
            <div className="research-tools-panel-body" style={{ display: 'grid', gap: 12 }}>
              {analytics.question_breakdown.map((qb, idx) => (
                <article
                  key={idx}
                  className="research-tools-survey-row"
                  style={{
                    ['--s' as string]: '#41d2ff',
                    gridTemplateColumns: '1fr',
                    alignItems: 'start',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <strong>
                        {idx + 1}. {qb.title}
                      </strong>
                      <small>{questionTypeLabel(qb.type)}</small>
                    </div>
                    <span className="research-tools-badge">{questionTypeLabel(qb.type)}</span>
                  </div>
                  {Object.keys(qb.distribution).length === 0 ? (
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: '#8faebb' }}>
                      Ответов на этот вопрос пока нет
                    </p>
                  ) : (
                    <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                      {Object.entries(qb.distribution).map(([ansText, cnt]) => {
                        const pct =
                          analytics.total_responses > 0
                            ? Math.round((cnt / analytics.total_responses) * 100)
                            : 0;
                        return (
                          <div
                            key={ansText}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'minmax(0, 1fr) minmax(80px, 1.2fr) 64px',
                              gap: 10,
                              alignItems: 'center',
                              fontSize: 12,
                            }}
                          >
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {ansText}
                            </span>
                            <div
                              style={{
                                height: 8,
                                background: 'rgba(255,255,255,.08)',
                                borderRadius: 4,
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  width: `${pct}%`,
                                  height: '100%',
                                  background: 'linear-gradient(90deg,#41d2ff,#2ea8df)',
                                }}
                              />
                            </div>
                            <span style={{ textAlign: 'right', color: '#8faebb' }}>
                              {cnt} ({pct}%)
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="research-tools-btn" onClick={onClose}>
              Закрыть
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
